import { JSX, useContext } from "react";
import { isEqual } from "lodash";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared";
import { CompetitiveInsightsManageContext } from "@app/overviews/network-benchmarking-dashboard/manage/CompetitiveInsightsManageContext";
import { CompetitiveInsightsManageProjectStatusEnum } from "@app/overviews/network-benchmarking-dashboard/manage/shared/CompetitiveInsightsManageProjectStatusEnum";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESButton } from "@app/shared/qes-standards/Button";

const CompetitiveInsightsManageFilters = (): JSX.Element => {
  const competitiveInsightsManageContext = useContext(CompetitiveInsightsManageContext);

  const clearDisabled = (): boolean => {
    if (competitiveInsightsManageContext.projectsLoading) {
      return true;
    }

    return (
      competitiveInsightsManageContext.selectedActive.length === competitiveInsightsManageContext.activeOptions.length &&
      competitiveInsightsManageContext.selectedLinesOfBusiness.length === competitiveInsightsManageContext.lineOfBusinessOptions.length &&
      competitiveInsightsManageContext.selectedStates.length === competitiveInsightsManageContext.stateOptions.length &&
      competitiveInsightsManageContext.selectedClients.length === competitiveInsightsManageContext.clientOptions.length
    );
  }

  const applyDisabled = (): boolean => {
    if (competitiveInsightsManageContext.projectsLoading) {
      return true;
    }

    const activeEqual = isEqual(
      competitiveInsightsManageContext.selectedActive.map(x => x.key).sort(),
      [...competitiveInsightsManageContext.displayedFilters.selectedActive].sort()
    );

    const linesOfBusinessEqual = isEqual(
      competitiveInsightsManageContext.selectedLinesOfBusiness.map(x => x.key).sort(),
      [...competitiveInsightsManageContext.displayedFilters.selectedLinesOfBusiness].sort()
    );

    const statesEqual = isEqual(
      competitiveInsightsManageContext.selectedStates.map(x => x.key).sort(),
      [...competitiveInsightsManageContext.displayedFilters.selectedStates].sort()
    );

    const clientsEqual = isEqual(
      competitiveInsightsManageContext.selectedClients.map(x => x.key).sort(),
      [...competitiveInsightsManageContext.displayedFilters.selectedClients].sort()
    );

    // in order to avoid unnecessary re-applies, prevent the user from applying filters if
    // a.) the currently-selected filters are the same as those that were used for what is already displayed
    // b.) at least one active status, line of business, state, and client are not selected
    const anyChanged = !activeEqual || !linesOfBusinessEqual || !statesEqual || !clientsEqual;
    const allSelected = competitiveInsightsManageContext.selectedActive.length > 0 &&
      competitiveInsightsManageContext.selectedLinesOfBusiness.length > 0 &&
      competitiveInsightsManageContext.selectedStates.length > 0 &&
      competitiveInsightsManageContext.selectedClients.length > 0;

    return !(anyChanged && allSelected);
  }

  const onClearClick = (): void => {
    competitiveInsightsManageContext.onClearCompetitiveInsightsManageFiltersClick();
  }

  return (
    <div className="row">
      <div className="col-12">
        <div className="card card-static">
          <div className="card-body">
            <div className="row">
              <div className="col-12">
                <label className="label-m mb-2">
                  Filters
                </label>
              </div>
            </div>
            <div className="row">
              <div className="col-md-10">
                <div className="row">
                  <div className="col-md-3">
                    <MultiselectDropdown
                      containerClassName="d-block"
                      customSelectInputText={(!!competitiveInsightsManageContext.lineOfBusinessOptions.length && competitiveInsightsManageContext.lineOfBusinessOptions.length === competitiveInsightsManageContext.selectedLinesOfBusiness.length) ? "All" : null}
                      disabled={competitiveInsightsManageContext.projectsLoading}
                      items={competitiveInsightsManageContext.lineOfBusinessOptions}
                      label="Line of Business"
                      onSelectionChanged={(selectedItems: DropdownItem<BenchmarkingLineOfBusinessEnum>[]) => { competitiveInsightsManageContext.setSelectedLinesOfBusiness(selectedItems) }}
                      selectedItems={competitiveInsightsManageContext.selectedLinesOfBusiness}
                      testId="competitive-insights__manage--filters-line-of-business"
                    />
                  </div>
                  <div className="col-md-3">
                    <MultiselectDropdown
                      containerClassName="d-block"
                      customSelectInputText={(!!competitiveInsightsManageContext.stateOptions.length && competitiveInsightsManageContext.stateOptions.length === competitiveInsightsManageContext.selectedStates.length) ? "All" : null}
                      disabled={competitiveInsightsManageContext.projectsLoading}
                      items={competitiveInsightsManageContext.stateOptions}
                      label="State"
                      onSelectionChanged={(selectedItems: DropdownItem<string>[]) => { competitiveInsightsManageContext.setSelectedStates(selectedItems) }}
                      selectedItems={competitiveInsightsManageContext.selectedStates}
                      testId="competitive-insights__manage--filters-state"
                    />
                  </div>
                  {competitiveInsightsManageContext.isQuestInternal && (
                    <div className="col-md-3">
                      <MultiselectDropdown
                        containerClassName="d-block"
                        customSelectInputText={(!!competitiveInsightsManageContext.clientOptions.length && competitiveInsightsManageContext.clientOptions.length === competitiveInsightsManageContext.selectedClients.length) ? "All" : null}
                        disabled={competitiveInsightsManageContext.projectsLoading}
                        items={competitiveInsightsManageContext.clientOptions}
                        label="Client"
                        onSelectionChanged={(selectedItems: DropdownItem<string>[]) => { competitiveInsightsManageContext.setSelectedClients(selectedItems) }}
                        selectedItems={competitiveInsightsManageContext.selectedClients}
                        testId="competitive-insights__manage--filters-client"
                      />
                    </div>
                  )}
                  <div className="col-md-3">
                    <MultiselectDropdown
                      containerClassName="d-block"
                      customSelectInputText={(!!competitiveInsightsManageContext.activeOptions.length && competitiveInsightsManageContext.activeOptions.length === competitiveInsightsManageContext.selectedActive.length) ? "All" : null}
                      disabled={competitiveInsightsManageContext.projectsLoading}
                      items={competitiveInsightsManageContext.activeOptions}
                      label="Active"
                      onSelectionChanged={(selectedItems: DropdownItem<CompetitiveInsightsManageProjectStatusEnum>[]) => { competitiveInsightsManageContext.setSelectedActive(selectedItems) }}
                      selectedItems={competitiveInsightsManageContext.selectedActive}
                      testId="competitive-insights__manage--filters-active"
                    />
                  </div>
                </div>
              </div>
              <div className="col-md-2 d-flex align-items-end">
                <div>
                  <QESButton
                    disabled={applyDisabled()}
                    qesTestId="competitive-insights__manage--filters-apply"
                    qesType="secondary"
                    onClick={() => competitiveInsightsManageContext.onApplyFiltersClick()}
                    className="pb-0"
                  >
                    Apply
                  </QESButton>

                  <QESButton
                    disabled={clearDisabled()}
                    qesTestId="competitive-insights__manage--filters-clear"
                    qesType="secondary-danger"
                    onClick={() => onClearClick()}
                    className="pb-0"
                  >
                    Clear
                  </QESButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompetitiveInsightsManageFilters;
